import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, ArrowLeft, Chrome, Loader2 } from 'lucide-react';
import { initGoogleButton } from '../services/googleAuth';

export function AdminLogin() {
  const { 
    isAuthenticated, 
    isAuthorized, 
    error, 
    isScriptLoaded, 
    handleGoogleResponse,
    login
  } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const buttonRenderedRef = useRef(false);
  const [showFallback, setShowFallback] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (isAuthenticated && isAuthorized) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, isAuthorized, navigate]);

  // Afficher le fallback après un délai si le bouton ne s'affiche pas
  useEffect(() => {
    if (isScriptLoaded) {
      const timer = setTimeout(() => {
        if (!buttonRenderedRef.current) {
          console.log('[AdminLogin] Showing fallback button');
          setShowFallback(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded]);

  // Essayer de rendre le bouton Google quand tout est prêt
  useEffect(() => {
    if (!isScriptLoaded) return;
    if (buttonRenderedRef.current) return;
    if (!buttonRef.current) return;

    // Utiliser setTimeout pour éviter setState pendant le render
    const timer = setTimeout(() => {
      try {
        console.log('[AdminLogin] Initializing Google button...');
        initGoogleButton('google-signin-button', (response) => {
          console.log('[AdminLogin] Google response received');
          handleGoogleResponse(response);
        });
        buttonRenderedRef.current = true;
        setShowFallback(false);
        console.log('[AdminLogin] Google button rendered');
      } catch (err) {
        console.error('[AdminLogin] Error rendering Google button:', err);
        setScriptError('Erreur lors de l\'affichage du bouton Google');
        setShowFallback(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isScriptLoaded, handleGoogleResponse]);

  const handleManualLogin = () => {
    login();
  };

  const displayError = error || scriptError;

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Retour au portfolio */}
        <a 
          href="/" 
          className="flex items-center gap-2 text-[#5e5e5e] hover:text-[#1d1d1f] transition-colors mb-6 justify-center"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Retour au portfolio</span>
        </a>

        {/* Login Card */}
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1f75cb]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#1f75cb]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1d1d1f] mb-2">
              Espace Admin
            </h1>
            <p className="text-[#5e5e5e] text-sm">
              Connexion sécurisée requise
            </p>
          </div>

          {/* Error Message */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{displayError}</p>
            </motion.div>
          )}

          {/* Google Sign In Button Container */}
          <div className="min-h-[50px]">
            {!isScriptLoaded ? (
              <div className="w-full h-12 bg-[#f5f5f7] rounded-lg flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 text-[#1f75cb] animate-spin" />
                <span className="text-sm text-[#5e5e5e]">Chargement...</span>
              </div>
            ) : (
              <>
                {/* Container pour le bouton Google */}
                <div 
                  id="google-signin-button" 
                  ref={buttonRef}
                  className="flex justify-center"
                />
                
                {/* Bouton fallback si le bouton Google ne s'affiche pas */}
                {showFallback && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={handleManualLogin}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border border-[#e5e5e5] rounded-xl text-[#1d1d1f] font-medium hover:bg-[#f9f9f9] hover:border-[#d1d1d1] transition-all"
                  >
                    <Chrome className="w-5 h-5 text-[#4285F4]" />
                    <span>Continuer avec Google</span>
                  </motion.button>
                )}
              </>
            )}
          </div>

          {/* Debug info (visible en dev) */}
          {import.meta.env.DEV && (
            <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-600 font-mono space-y-1">
              <p>Script loaded: {isScriptLoaded ? '✅' : '❌'}</p>
              <p>Fallback shown: {showFallback ? '✅' : '❌'}</p>
              <p>Has client ID: {import.meta.env.VITE_GOOGLE_CLIENT_ID ? '✅' : '❌'}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#8e8e93] mt-6">
          © {new Date().getFullYear()} Pierre Touzeau • Administration
        </p>
      </motion.div>
    </div>
  );
}
